/**
 * AgentSidebar - Left panel showing sessions list and new session button.
 *
 * Phase 2: Added tab strip for Conversations | Suggestions.
 * When 'proposals' tab is active, renders SuggestionsList instead of sessions.
 */

import React, { useState } from 'react';
import { Box, Button, Typography, Divider } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import SessionsList from './SessionsList';
import SuggestionsList from '../SuggestionsList/SuggestionsList';
import './AgentSidebar.css';

/**
 * AgentSidebar Component
 *
 * @param {Object} props
 * @param {Array} props.sessions - All sessions for the current trip
 * @param {string|null} props.currentSessionId - Currently active session ID
 * @param {Function} props.onNewSession - Start a new session
 * @param {Function} props.onSelectSession - Select a session
 * @param {Function} props.onArchiveSession - Archive a session
 * @param {Function} props.onRestoreSession - Restore an archived session
 * @param {Function} props.onDeleteSession - Permanently delete a session
 * @param {Array}  [props.proposals=[]] - All suggestion docs for the trip
 * @param {Function} [props.onApprove] - (suggestionId) => void
 * @param {Function} [props.onDismiss] - (suggestionId) => void
 * @param {Function} [props.onModify] - (suggestionId, note) => void
 * @param {boolean} [props.canApprove=true] - Whether current user can approve proposals
 */
const AgentSidebar = ({
  sessions,
  currentSessionId,
  onNewSession,
  onSelectSession,
  onArchiveSession,
  onRestoreSession,
  onDeleteSession,
  proposals = [],
  onApprove,
  onDismiss,
  onModify,
  canApprove = true
}) => {
  const [activeTab, setActiveTab] = useState('conversations');

  const outstandingCount = proposals.filter((p) => p.status === 'outstanding').length;

  return (
    <Box className="agent-sidebar">
      {/* Tab strip */}
      <Box className="agent-sidebar-tabs">
        <button
          className={`agent-sidebar-tab ${activeTab === 'conversations' ? 'agent-sidebar-tab--active' : ''}`}
          onClick={() => setActiveTab('conversations')}
          type="button"
        >
          Conversations
        </button>
        <button
          className={`agent-sidebar-tab ${activeTab === 'proposals' ? 'agent-sidebar-tab--active' : ''}`}
          onClick={() => setActiveTab('proposals')}
          type="button"
        >
          Suggestions
          {outstandingCount > 0 && (
            <span className="agent-sidebar-tab-badge">{outstandingCount}</span>
          )}
        </button>
      </Box>

      {activeTab === 'conversations' && (
        <>
          {/* New session button */}
          <Box sx={{ px: 1.5, py: 1.25, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={onNewSession}
              fullWidth
              sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.8125rem' }}
            >
              New conversation
            </Button>
          </Box>

          <Divider />

          {/* Sessions list */}
          <SessionsList
            sessions={sessions}
            currentSessionId={currentSessionId}
            onSelectSession={onSelectSession}
            onArchiveSession={onArchiveSession}
            onRestoreSession={onRestoreSession}
            onDeleteSession={onDeleteSession}
          />
        </>
      )}

      {activeTab === 'proposals' && (
        <SuggestionsList
          suggestions={proposals}
          onApprove={onApprove}
          onDismiss={onDismiss}
          onModify={onModify}
          canApprove={canApprove}
        />
      )}
    </Box>
  );
};

export default AgentSidebar;
